/*
 * Copyright 2026 Sony Corporation
 */
import type { Device } from '@ips-jit/schema';
import { Box, Stack, Typography, type SvgIconProps } from '@mui/material';

import { AgentIcon, DevicesIcon, PackageIcon } from './icons';

export type Tone = 'ok' | 'busy' | 'bad' | 'off';
type Kind = 'agent' | 'app' | 'camera';

/** The icon's colour: green when it works, grey when it is off or not there yet, red when it broke. */
const COLOR: Record<Tone, string> = {
  ok: 'success.main',
  busy: 'text.disabled',
  bad: 'error.main',
  off: 'text.disabled',
};

const ICON: Record<Kind, (props: SvgIconProps) => React.JSX.Element> = {
  agent: AgentIcon,
  app: PackageIcon,
  camera: DevicesIcon,
};

const LABEL: Record<Kind, string> = { agent: 'Agent', app: 'App', camera: 'Camera' };

interface Row {
  key: string;
  kind: Kind;
  label: string;
  tone: Tone;
  text: string;
  detail?: string;
}

const row = (kind: Kind, tone: Tone, text: string, detail?: string, key: string = kind): Row => ({
  key,
  kind,
  label: LABEL[kind],
  tone,
  text,
  detail,
});

const clock = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const cameraTone = (state: Device['cameras'][number]['state']): Tone =>
  state === 'connected'
    ? 'ok'
    : state === 'connecting' || state === 'recovering' || state === 'needs_login'
      ? 'busy'
      : state === 'failed' || state === 'login_failed'
        ? 'bad'
        : 'off';

const CAMERA_STATE: Partial<Record<Device['cameras'][number]['state'], string>> = {
  needs_login: 'Needs a login',
  login_failed: 'Login failed',
};

/** Whether a camera waits for the user to set or fix its login in the runtime. */
export const needsLogin = (device: Device): boolean =>
  device.cameras.some((camera) => camera.state === 'needs_login' || camera.state === 'login_failed');

/** The rows of the status block: the agent, the app it runs, and one row per camera the app has talked to. */
export const statusRows = (device: Device): Row[] => {
  const online = device.status === 'online';
  const agent = row(
    'agent',
    online ? 'ok' : 'off',
    online ? 'Online' : device.last_seen ? `Offline, last seen ${clock(device.last_seen)}` : 'Never connected',
    [
      device.hostname,
      device.ip,
      device.agent_version ? `agent ${device.agent_version}` : null,
      device.credentials_version ? `credentials v${device.credentials_version}` : null,
    ]
      .filter(Boolean)
      .join(' · ') || undefined
  );

  const app = !online
    ? row('app', 'off', 'Unknown while offline')
    : !device.app
      ? row('app', 'off', device.running_version ? `Version ${device.running_version}` : 'Nothing delivered yet')
      : device.app.state === 'running'
        ? row(
            'app',
            'ok',
            `Version ${device.app.version} running`,
            device.app.since ? `since ${clock(device.app.since)}` : undefined
          )
        : device.app.state === 'crashed'
          ? row('app', 'bad', `Version ${device.app.version} crashed`, device.app.error)
          : row('app', 'off', `Version ${device.app.version} stopped`);

  const cameras =
    !online || device.cameras.length === 0
      ? [row('camera', 'off', online ? 'No session yet' : 'Unknown while offline')]
      : device.cameras.map((camera) =>
          row(
            'camera',
            cameraTone(camera.state),
            `${CAMERA_STATE[camera.state] ?? `${camera.state.charAt(0).toUpperCase()}${camera.state.slice(1)}`} · ${camera.address}${camera.transport === 'ssh' ? ' · SSH' : ''}`,
            [camera.model, camera.fingerprint, camera.error].filter(Boolean).join(' · ') || undefined,
            `camera-${camera.address}`
          )
        );

  return [agent, app, ...cameras];
};

/**
 * The one state that stands for the whole runtime, for a chip: off when the agent is, else the worst of the app and
 * the cameras (an app or camera that is not there yet is not a fault).
 */
export const worstTone = (device: Device): Tone => {
  if (device.status !== 'online') {
    return 'off';
  }
  const tones = statusRows(device).map((r) => r.tone);
  return tones.includes('bad') ? 'bad' : tones.includes('busy') ? 'busy' : 'ok';
};

/** The colour of each tone where it stands for the runtime as a whole (busy shows as amber there). */
export const TONE_COLOR: Record<Tone, string> = {
  ok: 'success.main',
  busy: 'warning.main',
  bad: 'error.main',
  off: 'text.disabled',
};

export const TONE_LABEL: Record<Tone, string> = { ok: 'ok', busy: 'in progress', bad: 'error', off: 'inactive' };

/** What the runtime's agent reports about itself, the app and the cameras: an icon coloured by its state and a line each. */
export const AgentStatus = ({ device }: { device: Device }) => (
  <Stack component="ul" spacing={1} sx={{ listStyle: 'none', p: 0, m: 0 }} aria-label="Runtime status">
    {statusRows(device).map((item) => {
      const Icon = ICON[item.kind];
      return (
        <Box component="li" key={item.key}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Icon titleAccess={TONE_LABEL[item.tone]} sx={{ fontSize: 18, color: COLOR[item.tone], flexShrink: 0 }} />
            <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
              <Box component="span" sx={{ color: 'text.secondary', mr: 0.75 }}>
                {item.label}
              </Box>
              {item.text}
            </Typography>
          </Stack>
          {item.detail && (
            // Past the icon (18 + 8 px), so it sits under the words.
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', pl: '26px', overflowWrap: 'anywhere' }}
            >
              {item.detail}
            </Typography>
          )}
        </Box>
      );
    })}
  </Stack>
);
